import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Box, Stack, Typography, CircularProgress } from "@mui/material";
import { CheckCircle2, XCircle } from "lucide-react";

import { reportGoogleOAuthResult } from "../../hooks/useGoogleOAuthPopup";

/**
 * Landing page for the Google OAuth round trip.
 *
 * The backend callback exchanges the code and then redirects the browser here.
 * This page runs inside the popup the connect dialog opened: it reports the
 * outcome to the dialog's tab and closes itself, so the dialog can refresh its account
 * list without losing the selection the consultant had already made.
 *
 * Public on purpose. It carries no token and does nothing but relay a message.
 * If it is ever reached in a normal tab, it says what happened and stays put.
 */
export function GoogleOAuthCallback() {
  const [params] = useSearchParams();
  // Spinner while we try to close; the outcome shows if the window stays open.
  const [isPopup, setIsPopup] = useState(true);

  const connected = params.get("connected");
  const error = params.get("error");

  useEffect(() => {
    // window.opener is usually null here (see reportGoogleOAuthResult), so the
    // result goes out over a same-origin channel instead.
    reportGoogleOAuthResult({ connected, error });
    window.close();
    // Still here: the browser refused to close it (e.g. opened as a normal tab).
    const timeout = setTimeout(() => setIsPopup(false), 400);
    return () => clearTimeout(timeout);
  }, [connected, error]);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 3,
      }}
    >
      <Stack spacing={2} alignItems="center" sx={{ maxWidth: 420, textAlign: "center" }}>
        {isPopup && <CircularProgress size={28} />}

        {!isPopup && error && (
          <>
            <XCircle size={40} color="#d32f2f" />
            <Typography variant="h6">No se ha podido conectar la cuenta</Typography>
            <Typography variant="body2" color="text.secondary">
              Google no ha completado la autorización. Vuelve a la ficha del
              cliente e inténtalo de nuevo desde «Añadir cuenta nueva».
            </Typography>
          </>
        )}

        {!isPopup && !error && (
          <>
            <CheckCircle2 size={40} color="#2e7d32" />
            <Typography variant="h6">Cuenta conectada</Typography>
            <Typography variant="body2" color="text.secondary">
              {connected
                ? `${connected} ya está disponible para vincular clientes.`
                : "La cuenta ya está disponible para vincular clientes."}
              {" "}Puedes cerrar esta pestaña.
            </Typography>
          </>
        )}
      </Stack>
    </Box>
  );
}

export default GoogleOAuthCallback;
